import React from 'react';
import { Link } from 'react-router-dom';
import {
  Target, Users, Handshake, GraduationCap, Leaf, Rocket,
  Lightbulb, Award, Landmark, Network, ClipboardList,
  CheckCircle2, ArrowRight, Globe, Zap, ShieldCheck
} from 'lucide-react';

const objectives = [
  { icon: Lightbulb, title: 'Vulgariser les solutions technologiques', desc: 'Promouvoir les solutions technologiques émergentes dans le BTP.' },
  { icon: Award, title: "Valoriser l'expertise nationale", desc: 'Mettre en lumière les entreprises locales innovantes du secteur.' },
  { icon: Handshake, title: 'Stimuler les partenariats', desc: 'Connecter entreprises, startups, centres techniques et investisseurs.' },
  { icon: GraduationCap, title: 'Développer les compétences', desc: 'Renforcer les compétences des professionnels du secteur.' },
  { icon: Leaf, title: 'Consommation responsable', desc: 'Encourager une utilisation optimisée et durable des matériaux.' },
  { icon: Rocket, title: 'Nouveaux modèles économiques', desc: "Favoriser l'émergence de modèles économiques innovants." },
];

const organisateurs = [
  { icon: Landmark, name: 'Confédération des PME/PMI du BTP', role: 'Promoteur URBANIK', desc: 'Promoteur principal et co-organisateur institutionnel. Porte-voix des PME du BTP, des matériaux et services connexes.' },
  { icon: Network, name: 'CCESP', role: 'Coordonnateur institutionnel', desc: 'Interface centrale entre l\'événement et l\'écosystème public, parapublic et diplomatique.' },
  { icon: ClipboardList, name: 'CHANIE', role: 'Co-organisateur', desc: 'Participation et structuration du Comité Scientifique. Encadrement technique des conférences et ateliers.' },
];

const Evenement = () => (
  <div className="min-h-screen pt-16">
    {/* PAGE HERO */}
    <section className="relative py-24 lg:py-32 bg-[#1E3A5F] overflow-hidden" data-testid="evenement-hero">
      <div className="absolute inset-0 opacity-10" aria-hidden="true" />
      <div className="relative container-institutional">
        <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-4">Le Forum</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">À Propos d'URBANIK</h1>
        <p className="text-lg text-white/70 max-w-2xl leading-relaxed">Une plateforme d'échanges, de formation et de collaboration dédiée à la transformation technologique du BTP.</p>
      </div>
    </section>

    {/* DESCRIPTION */}
    <section id="presentation" className="py-20 lg:py-28" data-testid="forum-description">
      <div className="container-institutional">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          <div className="lg:col-span-3">
            <h2 className="text-3xl font-bold text-[#1E3A5F] mb-6">Qu'est-ce que URBANIK ?</h2>
            <div className="w-16 h-1 bg-[#F5A623] rounded-full mb-8" />
            <p className="text-[#4A5568] leading-relaxed mb-5"><strong>URBANIK</strong> – Le Forum des Innovations Technologiques du Bâtiment et des Travaux Publics – est un rendez-vous professionnel majeur dédié à la transformation technologique du secteur du BTP et à la modernisation des chaînes de valeur.</p>
            <p className="text-[#6B7C93] leading-relaxed">L'événement se positionne comme une plateforme d'échanges, de formation, de collaboration et de mise en lumière des solutions technologiques qui façonnent le futur de la construction et des infrastructures.</p>
          </div>
          <div className="lg:col-span-2 space-y-4">
            {[
              { icon: Globe, text: 'Vitrine de solutions innovantes dans les technologies du BTP' },
              { icon: Users, text: 'Fertilisation croisée entre acteurs du secteur' },
              { icon: Lightbulb, text: "Pôle d'innovation valorisant des projets à fort potentiel" },
              { icon: ShieldCheck, text: "Place de marché numérique pour une continuité durable" },
            ].map((item, i) => (
              <div key={item.text} className="flex items-start gap-4 p-5 bg-[#F7F9FC] rounded-lg border border-[#E1E8F0]" data-testid={`forum-feature-${i}`}>
                <item.icon className="w-5 h-5 text-[#F5A623] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#4A5568] leading-relaxed">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* CONTEXTE & ENJEUX */}
    <section className="py-20 lg:py-28 bg-[#F7F9FC]" data-testid="contexte-section">
      <div className="container-institutional">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-4">Contexte & Enjeux</p>
            <h2 className="text-3xl font-bold text-[#1E3A5F] mb-6">Pourquoi maintenant ?</h2>
            <div className="w-16 h-1 bg-[#F5A623] rounded-full mb-8" />
            <p className="text-[#4A5568] leading-relaxed mb-5">Le secteur du BTP joue un rôle structurant dans le développement économique de la Côte d'Ivoire. Cependant, il fait face à plusieurs enjeux : croissance démographique, urbanisation rapide, besoin de durabilité, pression sur les ressources.</p>
            <p className="text-[#6B7C93] leading-relaxed">URBANIK se positionne comme un espace fédérateur pour orienter le secteur vers des solutions concrètes et durables.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[{ icon: Zap, text: 'Adoption accélérée des innovations' }, { icon: Leaf, text: 'Utilisation durable des matériaux' }, { icon: GraduationCap, text: 'Intégration des compétences techniques' }, { icon: Globe, text: 'Transformation numérique de la chaîne de valeur' }].map((item, i) => (
              <div key={item.text} className="bg-white border border-[#E1E8F0] rounded-lg p-5 hover:shadow-md transition-shadow" data-testid={`enjeu-card-${i}`}>
                <item.icon className="w-5 h-5 text-[#F5A623] mb-3" />
                <p className="text-sm text-[#4A5568]">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* THÈME PÉDAGOGIQUE */}
    <section id="president" className="py-20 lg:py-28" data-testid="theme-section">
      <div className="container-institutional">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-3">Thème 2026</p>
          <h2 className="text-3xl font-bold text-[#1E3A5F]">Thème Pédagogique</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#1E3A5F] rounded-xl p-10 text-center mb-10">
            <Target className="w-8 h-8 text-[#F5A623] mx-auto mb-4" />
            <p className="text-white text-lg font-medium italic leading-relaxed">"L'innovation technologique dans l'industrie du BTP et son impact sur la consommation de matériaux de construction à l'ère de la digitalisation"</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {['Optimiser l\'usage des matériaux', 'Durabilité des infrastructures', 'Réduction de l\'empreinte environnementale', 'Amélioration de la productivité et de la sécurité', 'Matériaux alternatifs et écoresponsables'].map((item) => (
              <div key={item} className="flex items-center gap-3 bg-white rounded-lg p-4 border border-[#E1E8F0]"><div className="w-2 h-2 bg-[#F5A623] rounded-full flex-shrink-0" /><span className="text-sm text-[#4A5568]">{item}</span></div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* OBJECTIFS */}
    <section id="objectifs" className="py-20 lg:py-28 bg-[#F7F9FC]" data-testid="objectifs-section">
      <div className="container-institutional">
        <div className="text-center mb-16">
          <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-3">Objectifs</p>
          <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4">Nos Objectifs</h2>
          <p className="text-[#6B7C93] max-w-2xl mx-auto">Promouvoir l'innovation et l'intégration technologique pour la filière BTP.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {objectives.map((obj, i) => (
            <div key={obj.title} className="p-8 bg-white border border-[#E1E8F0] rounded-lg hover:shadow-md transition-shadow" data-testid={`objective-card-${i}`}>
              <div className="w-12 h-12 rounded-lg bg-[#1E3A5F]/5 flex items-center justify-center mb-5"><obj.icon className="w-6 h-6 text-[#1E3A5F]" /></div>
              <h3 className="text-base font-bold text-[#1E3A5F] mb-2">{obj.title}</h3>
              <p className="text-sm text-[#6B7C93] leading-relaxed">{obj.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ORGANISATEURS */}
    <section className="py-20 lg:py-28" data-testid="organisateurs-section">
      <div className="container-institutional">
        <div className="text-center mb-16">
          <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-3">Organisation</p>
          <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4">Organisateurs</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {organisateurs.map((org, i) => (
            <div key={org.name} className="text-center p-8 bg-white border border-[#E1E8F0] rounded-lg hover:shadow-md transition-shadow" data-testid={`org-card-${i}`}>
              <div className="w-14 h-14 rounded-full bg-[#1E3A5F]/5 flex items-center justify-center mx-auto mb-5"><org.icon className="w-7 h-7 text-[#1E3A5F]" /></div>
              <h3 className="text-lg font-bold text-[#1E3A5F] mb-1">{org.name}</h3>
              <p className="text-sm font-semibold text-[#F5A623] mb-4">{org.role}</p>
              <p className="text-sm text-[#6B7C93] leading-relaxed">{org.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA SECTION */}
    <section className="py-20 lg:py-28 bg-[#1E3A5F]" data-testid="evenement-cta">
      <div className="container-institutional text-center">
        <h2 className="text-3xl font-bold text-white mb-4">Rejoignez URBANIK 2026</h2>
        <p className="text-white/70 max-w-xl mx-auto mb-8">Participez au plus grand forum dédié à l'innovation technologique du BTP en Afrique de l'Ouest.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/participer" className="btn-primary text-base px-8 py-4">Participer <ArrowRight className="w-5 h-5 ml-2" /></Link>
          <Link to="/exposition/devenir-exposant" className="btn-outline border-white text-white hover:bg-white hover:text-[#1E3A5F] text-base px-8 py-4">Devenir Exposant</Link>
        </div>
      </div>
    </section>
  </div>
);

export default Evenement;
